// Server functions for user operations
import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { db } from '@/src/server/db'
import { auth } from '@/auth'
import { getSessionFromRequest } from '@/src/server/lib/accessControl'
import { PERMISSIONS } from '@/permission.config'
import type { AppSession } from '@/src/types/auth.d'
import { hasPermission } from '@/src/server/lib/permissions'
import { logResourceAccess, logUnauthorizedAccess, logAdminAction } from '@/src/server/lib/securityMonitoring'
import { requireUserId } from '@/src/server/lib/accessControl'
import { z } from 'zod'
import { JsonHelpers } from '@/src/server/lib/prismaJsonHelpers'
import { getBaseUrl } from '@/src/lib/base-url'
import crypto, { randomBytes } from 'crypto'

// Zod schemas for input validation
const GetUserInput = z.object({
  id: z.string(),
})

const GetUserByEmailInput = z.object({
  email: z.string(),
})

const UpdateUserInput = z.object({
  id: z.string(),
  roleId: z.string().optional(),
  organization: z.string().optional(),
})

const ListUsersByOrgIdInput = z.object({
  orgId: z.string(),
})

const UpdateMyPreferencesInput = z.object({
  locale: z.string().optional(),
  // Add more preference fields here as needed
})

const ChangePasswordInput = z.object({
  oldPassword: z.string().min(1),
  newPassword: z.string().min(8),
})

// Input for updating current user's basic profile (self-update)
const UpdateMeInput = z.object({
  name: z.string().optional(),
  email: z.string().email().optional(),
  image: z.string().optional(),
})

// Input for inviting a new user
const InviteUserInput = z.object({
  email: z.string().email(),
  name: z.string().optional(),
  roleId: z.string().optional(),
  organizationId: z.string().optional(),
})

// Get current user
export const me = createServerFn({ method: 'GET' })
  .handler(async () => {
    const session = await getSessionFromRequest()
    if (!session?.user?.id) return null
    
    const user = await db.user.findUnique({
      where: { id: session.user.id },
      include: { 
        roleDetails: true,
        organization: true,
      },
    })
    
    if (!user) return null
    
    const { roleDetails, organization, ...userWithoutRoleDetails } = user
    const normalizedOrg = organization ? {
      ...organization,
      data: JsonHelpers.normalizeRecordToObjectValues(
        JsonHelpers.organizationData.fromDb(organization.data)
      ),
    } : null
    
    return {
      ...userWithoutRoleDetails,
      organization: normalizedOrg,
      role: roleDetails || undefined,
    }
  })

// List all users
export const listUsers = createServerFn({ method: 'GET' })
  .handler(async () => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    
    if (!await hasPermission(session, [PERMISSIONS.ADMIN, PERMISSIONS.USERS_MANAGE, PERMISSIONS.ORG_USERS_MANAGE])) {
      await logUnauthorizedAccess(userId, 'user', undefined, { action: 'list users' })
      throw new Error('Unauthorized')
    }
    
    // System admins see all users, org admins see only their org users
    const isSystemAdmin = await hasPermission(session, [PERMISSIONS.ADMIN, PERMISSIONS.USERS_MANAGE])
    let whereClause: any = {}
    
    if (!isSystemAdmin && userId) {
      // Organization admin - filter to their organization
      const user = await db.user.findUnique({
        where: { id: userId },
        select: { organizationId: true },
      })
      
      if (user?.organizationId) {
        whereClause = { organizationId: user.organizationId }
        await logAdminAction(userId!, 'List organization users', { organizationId: user.organizationId })
      } else {
        // No organization - shouldn't have ORG_USERS_MANAGE permission
        throw new Error('Unauthorized')
      }
    } else {
      await logAdminAction(userId!, 'List all users', {})
    }
      
      const users = await db.user.findMany({
        where: whereClause,
        include: { roleDetails: true, organization: true },
      })
      
      const result = users.map(u => ({
        id: u.id,
        name: u.name,
        email: u.email,
        emailVerified: u.emailVerified,
        image: u.image,
        createdAt: typeof u.createdAt === 'string' ? u.createdAt : u.createdAt.toISOString(),
        updatedAt: typeof u.updatedAt === 'string' ? u.updatedAt : u.updatedAt.toISOString(),
        organizationId: u.organizationId,
        roleDetailsId: u.roleDetailsId,
        role: u.roleDetails ? {
          id: u.roleDetails.id,
          role: u.roleDetails.role,
          name: u.roleDetails.name,
          description: u.roleDetails.description,
          permissions: u.roleDetails.permissions,
        } : null,
        organization: u.organization ? {
          id: u.organization.id,
          name: u.organization.name,
          description: u.organization.description,
          domain: u.organization.domain,
          autoJoin: u.organization.autoJoin,
          logo: u.organization.logo,
          ownerId: u.organization.ownerId,
          data: JsonHelpers.normalizeRecordToObjectValues(JsonHelpers.organizationData.fromDb(u.organization.data)),
        } : null,
      }))
      
      return result
  })

// Get user by ID
export const getUser = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetUserInput.parse(data))
  .handler(async ({ data }) => {
    const user = await db.user.findUnique({
      where: { id: data.id },
      include: { roleDetails: true, organization: true },
    })
    if (!user) return null
    
    const { roleDetails, organization, ...userWithoutRoleDetails } = user
    return {
      ...userWithoutRoleDetails,
      role: roleDetails ? {
        id: roleDetails.id,
        role: roleDetails.role,
        name: roleDetails.name,
        description: roleDetails.description,
        permissions: roleDetails.permissions,
      } : undefined,
      organization: organization ? {
        id: organization.id,
        name: organization.name,
        description: organization.description,
        domain: organization.domain,
        autoJoin: organization.autoJoin,
        logo: organization.logo,
        ownerId: organization.ownerId,
        data: JsonHelpers.normalizeRecordToObjectValues(JsonHelpers.organizationData.fromDb(organization.data)),
      } : undefined,
      createdAt: typeof user.createdAt === 'string' ? user.createdAt : user.createdAt.toISOString(),
      updatedAt: typeof user.updatedAt === 'string' ? user.updatedAt : user.updatedAt.toISOString(),
    }
  })

// Get user by email
export const getUserByEmail = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetUserByEmailInput.parse(data))
  .handler(async ({ data }) => {
    const user = await db.user.findUnique({
      where: { email: data.email },
      include: { roleDetails: true },
    })
    if (!user) return null
    
    const { roleDetails, ...userWithoutRoleDetails } = user
    return {
      ...userWithoutRoleDetails,
      role: roleDetails || undefined,
    }
  })

// Update user (admin/org admin functionality)
export const updateUser = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => UpdateUserInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const currentUserId = session?.user?.id
    
    // Check permissions: system admin OR org admin
    const isSystemAdmin = await hasPermission(session, [PERMISSIONS.USERS_MANAGE, PERMISSIONS.ADMIN])
    const isOrgAdmin = await hasPermission(session, [PERMISSIONS.ORG_USERS_MANAGE])
    
    if (!isSystemAdmin && !isOrgAdmin) {
      throw new Error('Unauthorized')
    }
    
    // Prevent a user from changing their own role
    if (data.roleId && currentUserId === data.id) {
      throw new Error('You cannot change your own role.')
    }
    
    // Fetch the target user to check current role and organization
    const targetUser = await db.user.findUnique({
      where: { id: data.id },
      include: { roleDetails: true, organization: true },
    })
    if (!targetUser) throw new Error('User not found')
    
    // Organization admins can only manage users in their organization
    if (!isSystemAdmin && currentUserId) {
      const currentUser = await db.user.findUnique({
        where: { id: currentUserId },
        select: { organizationId: true },
      })
      
      if (currentUser?.organizationId !== targetUser.organizationId) {
        throw new Error('Unauthorized: You can only manage users in your organization')
      }
      
      // Org admins cannot change org membership
      if (data.organization !== undefined && data.organization !== targetUser.organization?.name) {
        throw new Error('Organization admins cannot change organization membership')
      }
    }
    
    const targetRole = targetUser.roleDetails?.role
    if (targetRole === 'SUPERUSER') {
      throw new Error('The role of SUPERUSER cannot be changed.')
    }
    
    // Only SYSTEM ADMINS can set ADMIN role or system-level roles
    if (data.roleId) {
      const newRole = await db.roleDetails.findUnique({ where: { id: data.roleId } })
      if (!newRole) throw new Error('Role not found')
      
      // System-level roles (ADMIN, SUPERUSER) require system admin
      if ((newRole.role === 'ADMIN' || newRole.role === 'SUPERUSER') && !isSystemAdmin) {
        throw new Error('Only system admins can assign system-level roles.')
      }
      
      // Org admins can only assign roles within their permission scope
      if (!isSystemAdmin) {
        const allowedOrgRoles = ['ORG_MEMBER', 'ORG_ADMIN']
        if (!allowedOrgRoles.includes(newRole.role)) {
          throw new Error('Organization admins can only assign ORG_MEMBER or ORG_ADMIN roles.')
        }
      }
    }
    
    const updateData: any = {}
    if (data.roleId !== undefined) {
      if (data.roleId) {
        updateData.roleDetailsId = data.roleId
      } else {
        updateData.roleDetailsId = null
      }
    }
    if (data.organization !== undefined) {
      if (data.organization) {
        const org = await db.organization.findUnique({ where: { name: data.organization } })
        if (!org) throw new Error('Organization not found')
        updateData.organizationId = org.id
        
        // Auto-assign ORG_MEMBER role when adding user to organization (if they don't have a role or have USER role)
        if (!data.roleId && (!targetUser.roleDetails || targetUser.roleDetails.role === 'USER')) {
          const orgMemberRole = await db.roleDetails.findUnique({ where: { role: 'ORG_MEMBER' } })
          if (orgMemberRole) {
            updateData.roleDetailsId = orgMemberRole.id
          }
        }
      } else {
        updateData.organizationId = null
        // When removing from organization, downgrade ORG_* roles to USER (unless system admin)
        if (!data.roleId && targetUser.roleDetails && targetUser.roleDetails.role.startsWith('ORG_')) {
          const userRole = await db.roleDetails.findUnique({ where: { role: 'USER' } })
          if (userRole) {
            updateData.roleDetailsId = userRole.id
          }
        }
      }
    }
    
    await db.user.update({
      where: { id: data.id },
      data: updateData,
    })
    
    const updated = await db.user.findUnique({
      where: { id: data.id },
      include: { roleDetails: true },
    })
    if (!updated) throw new Error('User not found after update')
    
    return {
      ...updated,
      role: updated.roleDetails ?? null,
      emailVerified: updated.emailVerified ?? false,
    }
  })

// List users by organization ID
export const listUsersByOrgId = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => ListUsersByOrgIdInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    if (!await hasPermission(session, [PERMISSIONS.ADMIN, PERMISSIONS.USERS_MANAGE, PERMISSIONS.ORG_USERS_MANAGE, PERMISSIONS.ORG_MANAGE_OWN])) {
      throw new Error('Unauthorized')
    }
    
    // If user only has org-level permissions (not system admin), restrict to their own org
    const userId = session?.user?.id
    if (userId) {
      const isSystemAdmin = await hasPermission(session, [PERMISSIONS.ADMIN, PERMISSIONS.USERS_MANAGE])
      if (!isSystemAdmin) {
        const user = await db.user.findUnique({
          where: { id: userId },
          select: { organizationId: true },
        })
        if (user?.organizationId !== data.orgId) {
          throw new Error('Not authorized to access users from another organization')
        }
      }
    }
    
    const users = await db.user.findMany({
      where: { organizationId: data.orgId },
      include: { roleDetails: true, organization: true },
    })
    
    return users.map(u => {
      const normalizedOrg = u.organization ? {
        ...u.organization,
        data: JsonHelpers.normalizeRecordToObjectValues(
          JsonHelpers.organizationData.fromDb(u.organization.data)
        ),
      } : undefined
      
      return {
        ...u,
        role: u.roleDetails || undefined,
        organization: normalizedOrg,
        emailVerified: u.emailVerified ?? false,
      }
    })
  })

// Update current user's own preferences (stored in JSONB preferences field)
// Update current user's own profile (name, email, image)
export const updateMe = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => UpdateMeInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    if (!userId) throw new Error('Not authenticated')

    // If changing email, enforce rules: no linked social accounts, and trigger verification flow
    const changingEmail = data.email !== undefined && data.email !== session?.user?.email

    if (changingEmail) {
      const newEmail = data.email
      if (typeof newEmail !== 'string') {
        throw new Error('Invalid email')
      }

      // Prevent change if any social account is linked (github/google/atlassian)
      const socialCount = await db.account.count({
        where: { userId, providerId: { in: ['github', 'google', 'atlassian'] } },
      })
      if (socialCount > 0) {
        throw new Error('Cannot change email while a social account is linked')
      }

      // Prevent changing to an email already in use
      const existing = await db.user.findUnique({ where: { email: newEmail } })
      if (existing && existing.id !== userId) {
        throw new Error('Email already in use')
      }

      // Update non-email fields immediately (name/image)
      const partialUpdate: any = {}
      if (data.name !== undefined) partialUpdate.name = data.name
      if (data.image !== undefined) partialUpdate.image = data.image
      if (Object.keys(partialUpdate).length > 0) {
        await db.user.update({ where: { id: userId }, data: partialUpdate })
      }

      // Call Better-Auth's changeEmail API directly (avoids HTTP round-trip and cookie forwarding)
      const request = getRequest()
      await auth.api.changeEmail({
        body: { newEmail },
        headers: request?.headers ?? new Headers(),
      })

      // Return a sentinel so the client knows verification was triggered
      return { verificationSent: true }
    }

    // No email change — regular immediate update (name/image)
    const updateData: any = {}
    if (data.name !== undefined) updateData.name = data.name
    if (data.image !== undefined) updateData.image = data.image

    if (Object.keys(updateData).length === 0) {
      throw new Error('Nothing to update')
    }

    await db.user.update({ where: { id: userId }, data: updateData })

    const user = await db.user.findUnique({ where: { id: userId }, include: { roleDetails: true, organization: true } })
    if (!user) throw new Error('User not found')

    const { roleDetails, organization, ...userWithoutRoleDetails } = user
    const normalizedOrg = organization ? {
      ...organization,
      data: JsonHelpers.normalizeRecordToObjectValues(
        JsonHelpers.organizationData.fromDb(organization.data)
      ),
    } : null

    return {
      ...userWithoutRoleDetails,
      organization: normalizedOrg,
      role: roleDetails || undefined,
    }
  })

export const changePassword = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => ChangePasswordInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    if (!userId) throw new Error('Not authenticated')

    // Find local credential account
    const account = await db.account.findFirst({ where: { userId, providerId: 'credential' } })
    if (!account || !account.password) {
      throw new Error('No local password set; use password reset')
    }

    // Verify old password
    const bcrypt = await import('bcrypt')
    const match = await bcrypt.compare(data.oldPassword, account.password)
    if (!match) throw new Error('Current password is incorrect')

    // Hash and update new password
    const hashed = await bcrypt.hash(data.newPassword, 10)
    await db.account.update({ where: { id: account.id }, data: { password: hashed } })

    return { success: true }
  })

export const updateMyPreferences = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => UpdateMyPreferencesInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    if (!userId) throw new Error('Not authenticated')
    
    // Read current preferences
    const current = await db.user.findUnique({
      where: { id: userId },
      select: { preferences: true },
    })
    
    const currentPrefs = (current?.preferences as Record<string, any>) || {}
    const newPrefs = { ...currentPrefs }
    
    if (data.locale !== undefined) {
      newPrefs.locale = data.locale || 'en'
    }
    
    const updated = await db.user.update({
      where: { id: userId },
      data: { preferences: newPrefs as any },
      select: { id: true, preferences: true },
    })
    
    return updated
  })

// Invite a new user (admin only)
export const inviteUser = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => InviteUserInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const currentUserId = session?.user?.id
    
    // Check permissions: system admin OR org admin
    const isSystemAdmin = await hasPermission(session, [PERMISSIONS.USERS_MANAGE, PERMISSIONS.ADMIN])
    const isOrgAdmin = await hasPermission(session, [PERMISSIONS.ORG_USERS_MANAGE])
    
    if (!isSystemAdmin && !isOrgAdmin) {
      throw new Error('Unauthorized')
    }
    
    // Check if user already exists
    const existingUser = await db.user.findUnique({
      where: { email: data.email },
    })
    
    if (existingUser) {
      throw new Error('A user with this email already exists')
    }
    
    // Org admins can only invite to their organization
    let organizationId = data.organizationId
    if (!isSystemAdmin && currentUserId) {
      const currentUser = await db.user.findUnique({
        where: { id: currentUserId },
        select: { organizationId: true },
      })
      
      if (!currentUser?.organizationId) {
        throw new Error('Organization admins must be part of an organization')
      }
      
      // Force the invitation to be for the org admin's organization
      organizationId = currentUser.organizationId
    }
    
    // Validate role if provided
    let roleDetailsId = data.roleId
    if (roleDetailsId) {
      const role = await db.roleDetails.findUnique({ where: { id: roleDetailsId } })
      if (!role) throw new Error('Role not found')
      
      // Org admins can only assign org-level roles
      if (!isSystemAdmin) {
        const allowedOrgRoles = ['ORG_MEMBER', 'ORG_ADMIN']
        if (!allowedOrgRoles.includes(role.role)) {
          throw new Error('Organization admins can only assign ORG_MEMBER or ORG_ADMIN roles')
        }
      }
    } else {
      // Default role: ORG_MEMBER if part of org, USER otherwise
      if (organizationId) {
        const orgMemberRole = await db.roleDetails.findUnique({ where: { role: 'ORG_MEMBER' } })
        roleDetailsId = orgMemberRole?.id
      } else {
        const userRole = await db.roleDetails.findUnique({ where: { role: 'USER' } })
        roleDetailsId = userRole?.id
      }
    }
    
    // Create user without password (password will be set via reset link)
    // Generate a cuid-like ID (same pattern as seed.ts)
    const userId = crypto.randomUUID().replace(/-/g, '').slice(0, 24)
    
    const user = await db.user.create({
      data: {
        id: userId,
        email: data.email,
        name: data.name || data.email.split('@')[0],
        emailVerified: false,
        organizationId,
        roleDetailsId,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      include: {
        roleDetails: true,
        organization: true,
      },
    })
    
    // Create default API key
    try {
      // Use crypto.randomBytes for cryptographically secure key generation
      const key = randomBytes(32).toString('hex')
      await db.apiKey.create({
        data: {
          key,
          name: 'Default Key',
          userId: user.id,
        },
      })
    } catch (error) {
      console.error('Failed to create default API key for invited user:', error)
    }
    
    // Trigger password reset flow to send invitation email
    try {
      await auth.api.requestPasswordReset({
        body: {
          email: data.email,
          redirectTo: `${getBaseUrl()}/reset-password`,
        },
      })
    } catch (error) {
      console.error('Failed to send invitation email:', error)
      // Don't fail the whole operation if email fails
    }
    
    await logAdminAction(
      currentUserId!,
      'Invite user',
      { invitedUserId: user.id, email: data.email, organizationId }
    )
    
    return {
      ...user,
      role: user.roleDetails ?? null,
      emailVerified: user.emailVerified ?? false,
    }
  })

// Get pending project access approvals for the current user's projects
export const getPendingApprovals = createServerFn({ method: 'GET' })
  .handler(async () => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    if (!userId) return []

    // Find all pending access tokens for projects owned by or linked to this user
    const myProjects = await db.project.findMany({
      where: {
        OR: [
          { createdById: userId },
          { users: { some: { id: userId } } },
        ],
      },
      select: { id: true, name: true },
    })

    if (myProjects.length === 0) return []

    const projectIds = myProjects.map(p => p.id)
    
    // Find verification tokens that are pending access requests for these projects
    const verifications = await db.verification.findMany({
      where: {
        identifier: { startsWith: 'project-access:' },
        expiresAt: { gt: new Date() },
      },
    })

    // Filter to only verifications for this user's projects
    const pending = []
    for (const v of verifications) {
      const parts = v.identifier.split(':')
      if (parts.length !== 3) continue
      const [, projectId, requestingUserId] = parts
      
      if (!projectIds.includes(projectId)) continue

      const requestingUser = await db.user.findUnique({
        where: { id: requestingUserId },
        select: { id: true, name: true, email: true },
      })

      const project = myProjects.find(p => p.id === projectId)

      if (requestingUser && project) {
        pending.push({
          id: v.id,
          token: v.value,
          projectId,
          projectName: project.name,
          requestingUser: {
            id: requestingUser.id,
            name: requestingUser.name,
            email: requestingUser.email,
          },
          createdAt: v.createdAt,
          expiresAt: v.expiresAt,
        })
      }
    }

    return pending
  })
